import {
  AppMenuProductInterface,
  AppMenuSavedDashboardInterface,
  AppMenuSubProductInterface,
  FetchAppMenuResponseInterface,
} from 'src/types/AppMenuInterface'
import { GetMenuInterface, MenuInterface } from 'src/types/MenuInterface'

/**
 * Transform program list to menu list
 */
export const transformProgramMenusFn = (menus: GetMenuInterface[]): MenuInterface[] => {
  return menus.reduce<MenuInterface[]>((acc, menu) => {
    if (menu.program_list?.length) {
      const programMenus: MenuInterface[] = menu.program_list.map(program => {
        return {
          ...menu,
          id: `${menu.id}-${program.id}`,
          programCode: `${menu.code}-${program.id}`,
          name: program.label,
          program,
          program_list: undefined,
          children: menu.children ? transformProgramMenusFn(menu.children) : undefined,
        }
      })
      return [...acc, ...programMenus]
    }
    return [
      ...acc,
      {
        ...menu,
        children: menu.children ? transformProgramMenusFn(menu.children) : undefined,
      },
    ]
  }, [])
}

export const findProductOnMenusFn = ({
  menus,
  productCode,
}: {
  menus: FetchAppMenuResponseInterface
  productCode: string
}): AppMenuProductInterface | null => {
  return menus.reduce<AppMenuProductInterface | null>((acc, category) => {
    if (acc) return acc
    return category.children?.find(c => c.code === productCode) || null
  }, null)
}

/**
 * Find a sub-product on menus
 */
export const findSubProductOnMenusFn = ({
  menus,
  productCode,
  subProductCode,
}: {
  menus: FetchAppMenuResponseInterface
  productCode?: string
  subProductCode: string
}): AppMenuSubProductInterface | null => {
  if (productCode) {
    const product = findProductOnMenusFn({ menus, productCode })
    if (!product) return null
    return product.children?.find(c => c.code === subProductCode) || null
  }

  const flattenProducts = getFlattedProductsFn(menus)

  return flattenProducts.reduce<AppMenuSubProductInterface | null>((acc, product) => {
    if (acc) return acc
    return product.children?.find(c => c.code === subProductCode) || null
  }, null)
}

/**
 * Get products from menus
 */
export const getFlattedProductsFn = (menu: FetchAppMenuResponseInterface) => {
  return menu.reduce<AppMenuProductInterface[]>((acc, category) => {
    if (category.children?.length) {
      return [...acc, ...category.children]
    }
    return acc
  }, [])
}

/**
 * Remove dashboard
 */
export const removeDashboardFromMenusFn = (
  menus: FetchAppMenuResponseInterface,
  dashboardId: string,
) => {
  return menus.map(category => {
    return {
      ...category,
      children: category.children?.map(child => {
        return {
          ...child,
          children: child.children?.map(subChild => {
            return {
              ...subChild,
              dashboards: subChild.dashboards?.filter(d => d.id !== dashboardId),
            }
          }),
        }
      }),
    }
  })
}

/**
 * Add dashboard to menus
 */
export const setSubProductDashboardsOnMenusFn = ({
  dashboards,
  menus,
  subProductCode,
  programId,
}: {
  menus: FetchAppMenuResponseInterface
  subProductCode: string
  dashboards: AppMenuSavedDashboardInterface[]
  programId?: string
}) => {
  return menus.map(category => {
    return {
      ...category,
      children: category.children?.map(child => {
        return {
          ...child,
          children: child.children?.map(subChild => {
            if (subChild.code !== subProductCode) {
              return subChild
            }

            if (programId) {
              return {
                ...subChild,
                dashboards: [
                  ...(subChild.dashboards?.filter(
                    d => d.program_id?.toString() !== programId?.toString(),
                  ) || []),
                  ...dashboards,
                ],
              }
            }

            return {
              ...subChild,
              dashboards,
            }
          }),
        }
      }),
    }
  })
}

/**
 * Update dashboard in menus
 */
export const updateDashboardInMenusFn = (
  menus: FetchAppMenuResponseInterface,
  dashboard: AppMenuSavedDashboardInterface,
) => {
  return menus.map(category => {
    return {
      ...category,
      children: category.children?.map(child => {
        return {
          ...child,
          children: child.children?.map(subChild => {
            return {
              ...subChild,
              dashboards: subChild.dashboards?.map(d => (d.id === dashboard.id ? dashboard : d)),
            }
          }),
        }
      }),
    }
  })
}

/**
 * Mark dashboard as favorites
 */
export const markDashboardAsFavoriteFn = ({
  dashboardId,
  menus,
  subProduct,
}: {
  menus: FetchAppMenuResponseInterface
  subProduct: string
  dashboardId: string
}) => {
  return menus.map(category => {
    return {
      ...category,
      children: category.children?.map(child => {
        return {
          ...child,
          children: child.children?.map(subChild => {
            if (subChild.code !== subProduct) {
              return subChild
            }
            return {
              ...subChild,
              dashboards: subChild.dashboards?.map(d =>
                d.id === dashboardId ? { ...d, favorite: true } : d,
              ),
            }
          }),
        }
      }),
    }
  })
}

/**
 * Find dashboards of a sub-product on menus
 */
export const findDashboardsOnMenusFn = ({
  menus,
  product,
  subProductCode,
  programId,
}: {
  menus: FetchAppMenuResponseInterface
  product?: string
  subProductCode: string
  programId?: string
}): AppMenuSavedDashboardInterface[] => {
  const subProduct = findSubProductOnMenusFn({
    menus,
    productCode: product,
    subProductCode,
  })

  if (!subProduct) return []

  if (!programId) {
    return subProduct.dashboards || []
  }

  return subProduct.dashboards?.filter(d => d.program_id?.toString() === programId.toString()) || []
}
